import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";

import { uploadInsuranceCardImages } from "src/api/client";
import type { QueueStatus } from "src/api/types";
import { StatusToast } from "src/components/StatusToast";
import { useInsuranceCardDraft } from "src/hooks/useInsuranceCardDraft";
import type { Side } from "src/hooks/useInsuranceCardDraft";

interface Props {
  clientId: string;
  streamToken: string;
  status: QueueStatus | null;
  onBack: () => void;
  onSubmitted: () => void;
}

const SLOTS: { side: Side; label: string }[] = [
  { side: "front", label: "Front of card" },
  { side: "back", label: "Back of card" },
];

const SERVER_RETAKE_MESSAGE =
  "We couldn't use one of the photos. Please retake both the front and the back and try again.";

// side is set when the problem is known to be one photo: errors raised while
// preparing it, or a backend rejection that names the image.
export function errorMessage(code: string, side?: Side): string {
  switch (code) {
    case "unsupported_format":
      return side
        ? `The ${side} photo must be a JPEG or PNG.`
        : "Please choose a JPEG or PNG photo.";
    case "image_unreadable":
      return side ? `We couldn't read the ${side} photo. Please retake it.` : SERVER_RETAKE_MESSAGE;
    case "image_too_large":
      return side ? `The ${side} photo is too large. Please retake it.` : SERVER_RETAKE_MESSAGE;
    case "invalid_image":
    case "infected_image":
      return side ? `We couldn't use the ${side} photo. Please retake it.` : SERVER_RETAKE_MESSAGE;
    case "invalid_or_expired_stream_token":
      return "This page has expired. Please reopen the link we sent you.";
    default:
      return "Something went wrong. Please try again.";
  }
}

function usePreviewUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      // The object URL is an external resource, created and revoked with the blob.
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function Slot(props: {
  label: string;
  blob: Blob | null;
  processing: boolean;
  disabled: boolean;
  error: string | null;
  onFile: (file: File) => void;
}) {
  const { label, blob, processing, disabled, error, onFile } = props;
  const previewUrl = usePreviewUrl(blob);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [error]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onFile(file);
  }

  function renderPicker(action: "take" | "choose") {
    const text = action === "take" ? "Take photo" : "Choose photo";
    return (
      <label className="btn btn-outline">
        <input
          type="file"
          className="visually-hidden"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          capture={action === "take" ? "environment" : undefined}
          aria-label={`${label}: ${text.toLowerCase()}`}
          disabled={disabled}
          onChange={handleChange}
        />
        {text}
      </label>
    );
  }

  return (
    <div className="insurance-slot">
      <p className="insurance-slot-title">{label}</p>
      {previewUrl && (
        <img className="insurance-preview" src={previewUrl} alt={`${label} preview`} />
      )}
      {processing && <p className="info-section-text">Preparing photo…</p>}
      <div className="insurance-slot-actions">
        {renderPicker("take")}
        {renderPicker("choose")}
      </div>
      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="status-alert status-alert--warning insurance-error insurance-slot-error"
        >
          <p>{error}</p>
        </div>
      )}
    </div>
  );
}

export function InsuranceCardUpload({ clientId, streamToken, status, onBack, onSubmitted }: Props) {
  const { draft, loading, processing, setImage, clear } = useInsuranceCardDraft(clientId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slotErrors, setSlotErrors] = useState<Partial<Record<Side, string>>>({});
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === "completed" || status === "cancelled") onBack();
  }, [status, onBack]);

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [error]);

  async function handleFile(side: Side, file: File) {
    setError(null);
    setSlotErrors((prev) => ({ ...prev, [side]: undefined }));
    try {
      await setImage(side, file);
    } catch (e) {
      const message = errorMessage(e instanceof Error ? e.message : "", side);
      setSlotErrors((prev) => ({ ...prev, [side]: message }));
    }
  }

  async function handleSubmit() {
    if (!draft.front || !draft.back) return;
    setSubmitting(true);
    setError(null);
    try {
      await uploadInsuranceCardImages(clientId, streamToken, draft.front, draft.back);
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      const image = (e as { image?: Side }).image;
      if (image) {
        setSlotErrors((prev) => ({ ...prev, [image]: errorMessage(code, image) }));
      } else {
        setError(errorMessage(code));
      }
      setSubmitting(false);
      return;
    }
    await clear();
    onSubmitted();
  }

  const canSubmit = !loading && !!draft.front && !!draft.back && processing === null && !submitting;

  return (
    <div className="insurance-upload">
      {status === "in_progress" && (
        <StatusToast title="Your nurse is ready" message="Tap here to go back." onClick={onBack} />
      )}

      <button type="button" className="insurance-back" onClick={onBack}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Back
      </button>

      <h1>Upload your insurance card</h1>
      <p>Take a clear photo of the front and the back of your card.</p>

      {SLOTS.map(({ side, label }) => (
        <Slot
          key={side}
          label={label}
          blob={draft[side]}
          processing={processing === side}
          disabled={submitting}
          error={slotErrors[side] ?? null}
          onFile={(file) => void handleFile(side, file)}
        />
      ))}

      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="status-alert status-alert--warning insurance-error"
        >
          <p>{error}</p>
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary btn-full"
        disabled={!canSubmit}
        onClick={() => void handleSubmit()}
      >
        {submitting ? "Please wait..." : "Submit"}
      </button>
    </div>
  );
}
